import classNames from 'classnames';
import { ReactNode, useId, useLayoutEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import TabBar from '@alltrails/denali/components/TabBar';
import { SearchIds } from '../../../hooks/useSearchIds';
import { OnSelect, RenderResultContent, ResultBase, Results, SearchResultsEmptyState, SearchResultsTabBar } from '../../../types/searchBoxTypes';
import ResultItem from './ResultItem';
import SectionHeader from './SectionHeader';
import styles from './styles/CustomSearchResults.module.scss';

const EmptyState = ({ primary, secondary }: SearchResultsEmptyState) => {
  return (
    <div className={styles.emptyState}>
      <div className={styles.primary}>{primary}</div>
      <div className={styles.secondary}>{secondary}</div>
    </div>
  );
};

type CustomSearchResultsProps<T extends ResultBase> = {
  activeId?: string;
  areResultsLoading?: boolean;
  className?: string;
  emptyState?: SearchResultsEmptyState;
  onSelect: OnSelect<T>;
  preventScrollOnResultSelection?: boolean;
  query: string;
  results: Results<T>;
  renderAdditionalContent?: (query?: string) => ReactNode;
  renderResultContent: RenderResultContent<T>;
  searchResultsTabBar?: SearchResultsTabBar;
  searchIds?: SearchIds;
  afterRenderResults?: () => void;
};

const CustomSearchResults = <T extends ResultBase>({
  activeId,
  areResultsLoading,
  className,
  emptyState,
  onSelect,
  preventScrollOnResultSelection,
  query,
  results,
  renderAdditionalContent,
  renderResultContent,
  searchResultsTabBar,
  searchIds,
  afterRenderResults
}: CustomSearchResultsProps<T>) => {
  const { listboxId, searchLabelId, getOptionId } = searchIds || {};
  const searchCountId = useId();

  const tabBar = useMemo(() => {
    if (!searchResultsTabBar) {
      return null;
    }
    return <TabBar {...searchResultsTabBar} className={styles.tabBar} />;
  }, [searchResultsTabBar]);

  const content = useMemo(() => {
    if (results.length === 0 && !query) {
      if (emptyState) {
        return <EmptyState {...emptyState} />;
      }
      return null;
    }
    if (results.length) {
      return (
        <>
          <div id={searchCountId} className="screenReadersOnly" aria-atomic="true" aria-live="assertive">
            {results.length > 0 ? `${results.length} results found` : 'No results found'}
          </div>
          <ul
            role="listbox"
            {...(listboxId && { id: listboxId })}
            {...(searchLabelId && { 'aria-labelledby': searchLabelId })}
            {...(searchCountId && { 'aria-describedby': searchCountId })}
          >
            {results.map((result, index) => {
              if ('text' in result) {
                return <SectionHeader key={result.text} heading={result} />;
              }
              return (
                <li
                  key={result.id}
                  role="option"
                  aria-selected={activeId === result.id}
                  id={getOptionId ? getOptionId(result.id) : `${listboxId}-${result.id}`}
                >
                  <ResultItem
                    className={result.containerClassName}
                    isActive={activeId === result.id}
                    onClick={() => onSelect(result as T, { index, query })}
                    preventAutoScroll={preventScrollOnResultSelection}
                  >
                    {renderResultContent(result as T)}
                  </ResultItem>
                </li>
              );
            })}
          </ul>
        </>
      );
    }
    if (areResultsLoading) {
      return (
        <div className={styles.loadingState}>
          <LoadingDots />
        </div>
      );
    }
    return (
      <EmptyState
        primary={<FormattedMessage defaultMessage="No results" />}
        secondary={<FormattedMessage defaultMessage="We couldn't find anything matching {query}" values={{ query }} />}
      />
    );
  }, [
    activeId,
    areResultsLoading,
    emptyState,
    onSelect,
    preventScrollOnResultSelection,
    query,
    renderResultContent,
    results,
    getOptionId,
    listboxId,
    searchCountId,
    searchLabelId
  ]);

  useLayoutEffect(() => {
    afterRenderResults?.();
  }, [afterRenderResults]);

  return renderAdditionalContent || tabBar || content ? (
    // prevent blurring the input when clicking inside the dropdown
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={classNames(styles.container, className)} onMouseDown={e => e.preventDefault()}>
      <div className={styles.scrollable}>
        {renderAdditionalContent?.(query)}
        {tabBar}
        {content}
      </div>
    </div>
  ) : null;
};

export default CustomSearchResults;
